import { ToolApprovalCardBody } from "#components/ui/tool-approval/ToolApprovalCardBody.tsx";
import type { RowDecision } from "#components/ui/tool-approval/toolApprovalDecision.ts";
import { useSharedTranslation } from "#hooks/useSharedTranslation.ts";
import type { PendingApprovalCall, ToolApprovalEditError } from "#types/ToolApproval.ts";
import { Button, Flex } from "@hopper-ui/components";

interface Props {
    call: PendingApprovalCall;
    isBatch: boolean;
    showRowChoices: boolean;
    decision: RowDecision | undefined;
    onDecideRow: (toolCallId: string, decision: RowDecision) => void;
    edits: Record<string, unknown>;
    onEditArgs: (toolCallId: string, partial: Record<string, unknown>) => void;
    editErrors: ToolApprovalEditError[];
    onCardError: (toolCallId: string) => void;
}

/** Selection is carried by colour alone — changing variant or content resizes the button mid-decision. */
function rowChoiceClass(decision: RowDecision | undefined, option: RowDecision): string {
    if (decision === undefined) {
        return "wai-tool-approval-choice";
    }

    return decision === option
        ? `wai-tool-approval-choice is-chosen is-chosen-${option}`
        : "wai-tool-approval-choice is-unchosen";
}

/** One pending call: its content and, in decide-each mode, its own allow/deny. */
export function ToolApprovalRow({ call, isBatch, showRowChoices, decision, onDecideRow, edits, onEditArgs, editErrors, onCardError }: Props) {
    const { tShared } = useSharedTranslation();

    return (
        <Flex
            className={isBatch ? "wai-tool-approval-row" : undefined}
            alignItems={isBatch ? "center" : undefined}
            justifyContent="space-between"
            gap="inline-md"
            paddingY={isBatch ? "inset-sm" : undefined}
        >
            <ToolApprovalCardBody
                call={call}
                isBatch={isBatch}
                edits={edits}
                onEditArgs={partial => onEditArgs(call.toolCallId, partial)}
                editErrors={editErrors}
                onCardError={() => onCardError(call.toolCallId)}
            />
            {showRowChoices && (
                <Flex gap="inline-xs" shrink={0}>
                    <Button
                        variant="ghost-secondary"
                        size="sm"
                        aria-pressed={decision === "allow"}
                        className={rowChoiceClass(decision, "allow")}
                        onPress={() => onDecideRow(call.toolCallId, "allow")}
                    >
                        {tShared("toolApproval.allow")}
                    </Button>
                    <Button
                        variant="ghost-secondary"
                        size="sm"
                        aria-pressed={decision === "deny"}
                        className={rowChoiceClass(decision, "deny")}
                        onPress={() => onDecideRow(call.toolCallId, "deny")}
                    >
                        {tShared("toolApproval.deny")}
                    </Button>
                </Flex>
            )}
        </Flex>
    );
}
